import {
  Card,
  CardContent,
  Typography,
  Button,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  makeStyles,
  Box,
  Table,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  LinearProgress
} from '@material-ui/core';
import React, { FC, memo, useState } from 'react';
import { AlternatingColorTableRow } from 'src/components';
import { formatCurrency } from 'src/utils';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  GetProductsParams,
  VoucherProduct
} from 'src/redux/slices/voucher/types';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import useResolution from 'src/hooks/useResolution';
import { AlternateRowContainer } from 'src/views/product/component/AlternateRowContainer';
import { TextRow } from 'src/views/product/component/TextRow';
import { colors } from 'src/constants/colors';
import { PaginationV2 } from 'src/types';
import { Pagination } from '@material-ui/lab';

const useStyles = makeStyles(() => ({
  button: {
    marginTop: '2.5em',
    color: 'white',
    padding: '0.5em'
  },
  table: {
    border: '1px solid #a4abba'
  },

  tableCell: {
    textAlign: 'center',
    borderBottom: '1px solid #a4abba'
  },
  tableRow: {
    borderBottom: '1px solid #a4abba'
  }
}));

interface Props {
  voucherId?: number;
  isCreatePage?: boolean;
  isLoading?: boolean;
  meta?: PaginationV2;
  voucherProducts?: VoucherProduct[];

  searchProduct?: (payload: GetProductsParams) => void;
  setVoucherProducts: (arr: VoucherProduct[]) => void;
  setSelectedScopeIds: (arr: number[]) => void;
  deleteVoucherProduct: (deleteId?: number) => void;
  dynamicDiscountPrice: (val: number) => number | null;
  dynamicMarginPrice: (
    totalRetailPrice: number,
    totalDealersPrice: number
  ) => string;
}
const Component: FC<Props> = ({
  voucherId,
  isCreatePage,
  voucherProducts = [],
  isLoading = false,
  meta = {},

  searchProduct,
  deleteVoucherProduct,
  dynamicDiscountPrice,
  dynamicMarginPrice,
  setVoucherProducts,
  setSelectedScopeIds
}) => {
  const classes = useStyles();
  const { sm } = useResolution();

  const [keyword, setKeyword] = useState<string>('');
  const [params, setParams] = useState<GetProductsParams>({});

  const searchKeyword = () => {
    setParams({ keyword: keyword, page: 1 });
    searchProduct?.({ keyword: keyword, page: 1 });
  };

  return (
    <Card style={{ marginTop: '1em' }}>
      <CardContent>
        <Typography variant="h5">Products</Typography>
        <Grid
          container
          style={{
            gap: '1em',
            margin: '1em 0',
            justifyContent: 'space-between'
          }}
        >
          <Grid item xs md={3}>
            {isCreatePage ? (
              <Button
                color="secondary"
                variant="outlined"
                size="small"
                fullWidth
                style={{ height: '100%' }}
                onClick={() => {
                  setSelectedScopeIds([]);
                  setVoucherProducts([]);
                }}
              >
                Clear All
              </Button>
            ) : null}
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              variant="outlined"
              label="Search Products"
              disabled={isLoading}
              value={keyword}
              size="small"
              fullWidth
              onChange={(e) => setKeyword(e.target.value.trim() ?? '')}
              onKeyDown={(e) => {
                if (!isLoading) {
                  e.key === 'Enter' && searchKeyword();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
        <div
          style={{
            display: 'flex',
            justifyContent: 'end',
            marginBottom: '1em'
          }}
        >
          <Pagination
            disabled={isLoading}
            page={meta.current_page ?? 1}
            count={meta.last_page}
            onChange={(_, page) => searchProduct?.({ ...params, page })}
          />
        </div>
        <PerfectScrollbar style={{ maxHeight: '80vh' }}>
          {sm ? (
            <>
              {isLoading && <LinearProgress />}
              {voucherProducts?.[0] &&
                voucherProducts?.map((p, ind) => (
                  <AlternateRowContainer key={ind} index={ind}>
                    <div
                      style={{
                        padding: 10,
                        position: 'relative'
                      }}
                    >
                      {isCreatePage ? (
                        <TextRow
                          label=""
                          small
                          component={
                            <IconButton
                              style={{
                                cursor: 'pointer',
                                position: 'absolute',
                                borderRadius: '50px',
                                padding: 3,
                                backgroundColor: colors.common.white,
                                top: 10,
                                right: 10
                              }}
                              onClick={() => deleteVoucherProduct(p?.id)}
                            >
                              <DeleteIcon color="secondary" />
                            </IconButton>
                          }
                        />
                      ) : null}
                      <TextRow
                        label="Name"
                        small
                        component={
                          <div style={{ textAlign: 'center' }}>
                            <Typography style={{ fontSize: '.8em' }}>
                              {p?.name ?? ''}
                            </Typography>
                          </div>
                        }
                      />
                      <TextRow
                        label="Category"
                        small
                        value={p?.category ?? ''}
                      />
                      <TextRow
                        label="Dealers Price"
                        value={formatCurrency(p?.dealers_price ?? 0)}
                        small
                      />
                      <TextRow
                        label="Retail Price"
                        small
                        value={formatCurrency(p?.retail_price ?? 0)}
                      />
                      <TextRow
                        label="Discounted Price"
                        small
                        value={formatCurrency(p?.discounted_price ?? 0)}
                      />
                      <TextRow
                        label="Margin"
                        small
                        value={p?.margin}
                        noBorderBottom
                      />
                    </div>
                  </AlternateRowContainer>
                ))}
            </>
          ) : (
            <Box>
              {isLoading && <LinearProgress />}
              <Table className={classes.table} stickyHeader>
                <TableHead>
                  <TableRow className={classes.tableRow}>
                    {voucherId ? null : (
                      <TableCell className={classes.tableCell}></TableCell>
                    )}
                    <TableCell className={classes.tableCell}>
                      Product Name
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      Category
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      Dealers Price
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      Retail Price
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      Discounted Price
                    </TableCell>
                    <TableCell className={classes.tableCell}>Margin</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voucherProducts?.map((p) => {
                    return (
                      <AlternatingColorTableRow key={p.id}>
                        {voucherId ? null : (
                          <TableCell className={classes.tableCell}>
                            <DeleteIcon
                              onClick={() => deleteVoucherProduct(p?.id)}
                              color="secondary"
                              style={{ cursor: 'pointer' }}
                            />
                          </TableCell>
                        )}
                        <TableCell className={classes.tableCell}>
                          {p?.name ?? ''}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {p.category ?? ''}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {formatCurrency(p?.dealers_price ?? 0)}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {formatCurrency(p?.retail_price ?? 0)}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {isCreatePage
                            ? formatCurrency(
                                dynamicDiscountPrice(p.retail_price ?? 0)
                              )
                            : formatCurrency(p?.discounted_price ?? 0)}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {isCreatePage
                            ? dynamicMarginPrice(
                                p.retail_price ?? 0,
                                p.dealers_price ?? 0
                              )
                            : p?.margin ?? '0%'}
                        </TableCell>
                      </AlternatingColorTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </PerfectScrollbar>
        <div
          style={{
            display: 'flex',
            justifyContent: 'end',
            marginTop: '1em'
          }}
        >
          <Pagination
            page={meta.current_page ?? 1}
            count={meta.last_page}
            onChange={(_, page) => searchProduct?.({ ...params, page })}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export const VoucherProductsTable = memo(Component);
